import {
  DatePipe,
  DecimalPipe,
  HashLocationStrategy,
  LocationStrategy,
} from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { StoreModule } from '@ngrx/store';
import { storeDevtoolsImports } from './store-devtools-imports';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  init as sentryInit,
  BrowserTracing,
  createErrorHandler as sentryCreateErrorHandler,
  TraceService as SentryTraceService,
  routingInstrumentation as sentryRoutingInstrumentation,
} from '@sentry/angular-ivy';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { AppCommonModule } from './app-common/app-common.module';
import { AppRoutingModule } from './app-routing.module';
import { httpInterceptorProviders } from './authentication/interceptors/httpInterceptorProviders';
import { customTranslateLoader } from './services/custom-translate-loader';
import { appreciateReducer } from './state/appreciate/appreciate.reducer';

import { EffectsModule } from '@ngrx/effects';
import { AppComponent } from './app.component';
import { AuthEffects } from './store/auth/auth.effects';
import { AuthReducer } from './store/auth/auth.reducer';
import { cartReducer } from './store/cart/cart.reducers';
import { DeviceEffects } from './store/device/device.effects';
import { deviceReducer } from './store/device/device.reducer';
import { LayoutEffects } from './store/layout/layout.effects';
import { LayoutReducer } from './store/layout/layout.reducer';
import { RewardEffects } from './store/rewards/rewards.effects';
import {
  rewardDetailReducer,
  rewardsCatAndSubCatPreferenceReducer,
  rewardsReducer,
} from './store/rewards/rewards.reducer';
import { sharedReducer } from './store/shared/shared.reducer';
import { TemporarilyUnavailableModule } from './temporarily-unavailable/temporarily-unavailable.module';
import { ChatClientPanelModule } from './chat-client-panel/chat-client-panel.module';
import { ShowRedemptionInfoDialogModule } from './app-common/show-redemption-info-dialog/show-redemption-info-dialog.module';

const serverErrors = new RegExp(
  `500 Internal Server Error|502 Bad Gateway|503 Service Unavailable|504 Gateway Timeout`,
  'mi',
);

const errors401And404 = /Request failed with status code (401|404)/i;

if (environment.production) {
  sentryInit({
    dsn: 'https://4b96fe943552483a8b8a2d90c452a540@o1067282.ingest.sentry.io/4505160529018880',
    integrations: [
      new BrowserTracing({
        routingInstrumentation: sentryRoutingInstrumentation,
      }),
    ],
    beforeSend(event, hint) {
      const originalException = (hint && hint.originalException) as
        | { response?: { status?: number }; status?: number; message?: string }
        | undefined;

      const isNonErrorException =
        event.exception.values[0].value.startsWith(
          'Non-Error exception captured',
        ) ||
        originalException?.message?.startsWith(
          'Non-Error exception captured',
        ) === true;

      if (isNonErrorException) {
        return null;
      }

      const status =
        originalException?.response?.status ?? originalException?.status;

      if (status === 401 || status === 404) {
        return null;
      }
      return event;
    },

    environment: environment.settings.appName,
    tracesSampleRate: 0.01,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    ignoreErrors: [serverErrors, errors401And404],
  });
}

@NgModule({
  imports: [
    HttpClientModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    AppCommonModule,
    MatSidenavModule,
    ToastrModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: customTranslateLoader,
        deps: [HttpClient],
      },
    }),
    StoreModule.forRoot({
      appreciate: appreciateReducer,
      auth: AuthReducer,
      layout: LayoutReducer,
      cart: cartReducer,
      device: deviceReducer,
      shared: sharedReducer,
      rewards: rewardsReducer,
      reward: rewardDetailReducer,
      rewardsCatAndSubCatPreference: rewardsCatAndSubCatPreferenceReducer,
    }),
    EffectsModule.forRoot([
      AuthEffects,
      LayoutEffects,
      RewardEffects,
      DeviceEffects,
    ]),
    ...storeDevtoolsImports,
    TemporarilyUnavailableModule,
    ShowRedemptionInfoDialogModule,
    ChatClientPanelModule,
  ],
  declarations: [AppComponent],
  providers: [
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    DecimalPipe,
    DatePipe,
    httpInterceptorProviders,
    {
      provide: ErrorHandler,
      useValue: sentryCreateErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: SentryTraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [SentryTraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
